import { type SubmitHandler, useFormContext, useWatch } from 'react-hook-form';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { RecentUploads } from '@/components/UI/HookForm/Form/RecentUploads';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { useProjectPhases } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { ChangeInBudgetInput } from './ChangeInBudgetInput';
import { LineItemSelect } from './LineItemSelect';
import type { BudgetChangeOrderSchema } from './validationSchema';

interface BudgetHookFormProps {
  projectId: string;
  title: string;
  onSubmit: SubmitHandler<BudgetChangeOrderSchema>;
  isLoading?: boolean;
  hide: () => void;
  disableLineItem?: boolean;
}

const BudgetHookForm = ({ hide, title, onSubmit, projectId, isLoading, disableLineItem }: BudgetHookFormProps) => {
  const formMethods = useFormContext<BudgetChangeOrderSchema>();
  const recentFiles = useWatch({
    control: formMethods.control,
    name: 'recentFiles',
  });

  const onRemoveRecentFile = (index: number) => {
    if (!recentFiles) return;

    const newValue = [...recentFiles];
    newValue.splice(index, 1);
    formMethods.setValue('recentFiles', newValue);
  };

  const phases = useProjectPhases();
  const phaseOptions = phases.data?.data.map((phase) => ({
    label: phase.name,
    value: phase.slug,
  }));

  return (
    <HookForm onSubmit={onSubmit}>
      <h2 className='mt-0 capitalize p-6 pb-4 mb-0 border-b border-gray-300'>{title}</h2>
      <section className='grid grid-cols-2 gap-5 items-center overflow-y-auto max-h-[70dvh] p-6 pt-4'>
        <HookReactSingleSelect
          formMethods={formMethods}
          label='Phase'
          options={phaseOptions}
          placeholder=''
          name='phase'
          isDisabled={disableLineItem}
        />
        <LineItemSelect projectId={projectId} isDisabled={disableLineItem} />
        <HookInput formMethods={formMethods} label='Budgeted Amount ($)' disabled type='number' name='budgetedAmount' />
        <HookInput formMethods={formMethods} label='Proposed Amount ($)' type='number' name='proposedAmount' />
        <ChangeInBudgetInput />
        <HookTextarea formMethods={formMethods} wrapperClassName='col-span-2' label='Why is this Change Required?' name='reason' rows={3} />
        <div className='col-span-2'>
          <HookFileInput
            formMethods={formMethods}
            label='Template Documents'
            name='templateDocuments'
            multiple
            customClassName='overflow-hidden'
          />
          <RecentUploads recentFiles={recentFiles} onRemove={onRemoveRecentFile} />
        </div>
        <HookInput formMethods={formMethods} customClassName='col-span-2' label='Response Due By' type='date' name='responseDueBy' />
        <HookTextarea
          formMethods={formMethods}
          wrapperClassName='col-span-2'
          label='No Response Consequence'
          name='noResponseConsequence'
          rows={3}
        />
        <HookTextarea formMethods={formMethods} wrapperClassName='col-span-2' label='Notes' name='notes' rows={3} />
      </section>

      <div className='flex gap-2 justify-end p-6 border-t border-gray-300'>
        <Button label='Cancel' btnStyle='neutral' onClick={hide} />
        <Button label={isLoading ? 'Loading...' : 'Apply'} btnStyle='primary' btnType='submit' />
      </div>
    </HookForm>
  );
};

export default BudgetHookForm;
